import React, { useState, useEffect, useRef, useMemo } from "react";
import HeadingBox from "../../components/common/HeadingBox";
import styles from "./styles.module.scss";
import ServicesList from "../../components/services/ServicesList";
import { useLocation } from "react-router-dom";
import { fetchServicePage } from "../../utils/api";
import {
  animateServicesGrid,
  animateSubServices,
  animateMobileServices,
  refreshScrollTrigger,
} from "../../utils/animations";

const Arrow = ({ selected, onClick }) => (
  <div
    className={selected ? styles.arrowSelected : styles.arrow}
    onClick={onClick}
    tabIndex={0}
    role="button"
    aria-label="select service"
  >
    ▼
  </div>
);

const SubArrow = ({ open, onClick }) => (
  <div
    className={open ? styles.subArrowOpen : styles.subArrow}
    onClick={onClick}
    tabIndex={0}
    role="button"
    aria-label="expand sub-service description"
  >
    ▼
  </div>
);

const ServicesPage = ({ servicesData = [], isLoading = false }) => {
  const location = useLocation();
  const gridRef = useRef(null);
  const servicesGridRef = useRef(null);
  const subServicesRef = useRef(null);
  const mobileServicesRef = useRef(null);
  const [selectedIdx, setSelectedIdx] = useState(() => {
    if (location.state && typeof location.state.serviceId === 'number') {
      return location.state.serviceId;
    }
    return 0;
  });
  const [openSubService, setOpenSubService] = useState(null);
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth < 600 : false);
  const [servicePageData, setServicePageData] = useState({
    heading: "Your Journey to Renewal Begins Here",
    description: "Driven by science and guided by artistry, our treatments are designed to enhance your natural beauty with precision and care. Explore our range of face, body, and hair solutions — each tailored, tested, and delivered by expert professionals committed to your transformation."
  });
  
  // Ensure servicesData is an array - memoized to prevent unnecessary re-renders
  const safeServicesData = useMemo(() => {
    return Array.isArray(servicesData) ? servicesData : [];
  }, [servicesData]);
  const selected = selectedIdx !== null && selectedIdx >= 0 && safeServicesData.length > 0 ? safeServicesData[selectedIdx] : null;
  const cols = 7;

  // Log for debugging
  useEffect(() => {
    console.log('=== ServicesPage Debug ===');
    console.log('servicesData:', servicesData);
    console.log('servicesData type:', typeof servicesData);
    console.log('servicesData isArray:', Array.isArray(servicesData));
    console.log('safeServicesData:', safeServicesData);
    console.log('safeServicesData.length:', safeServicesData.length);
    if (safeServicesData.length > 0) {
      console.log('First service:', safeServicesData[0]);
    }
    console.log('=======================');
  }, [servicesData, safeServicesData]);

  // Fetch service page data
  useEffect(() => {
    const loadServicePageData = async () => {
      try {
        const data = await fetchServicePage();
        if (data) {
          setServicePageData((prev) => ({
            heading: data.heading || prev.heading,
            description: data.description || prev.description
          }));
        }
      } catch (error) {
        console.error("Failed to load service page data:", error);
        // Keep default values on error
      }
    };

    loadServicePageData();
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 600);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (location.state && typeof location.state.serviceId === 'number') {
      setSelectedIdx(location.state.serviceId);
      setTimeout(() => {
        if (gridRef.current) {
          gridRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 200);
    }
  }, [location.state]);

  useEffect(() => {
    if (selected && Array.isArray(selected.services) && selected.services.length > 0) {
      // On desktop, auto-open first service; on mobile, don't auto-open
      if (!isMobile) {
        setOpenSubService(0);
      }
    } else {
      setOpenSubService(null);
    }
  }, [selectedIdx, isMobile, selected]);

  // Animation for Services Grid (desktop)
  useEffect(() => {
    return animateServicesGrid(servicesGridRef, isMobile, isLoading, safeServicesData);
  }, [isMobile, isLoading, safeServicesData]);

  // Animation for Desktop Sub Services
  useEffect(() => {
    return animateSubServices(subServicesRef, isMobile, selected);
  }, [selectedIdx, isMobile, selected]);

  // Animation for Mobile Services
  useEffect(() => {
    return animateMobileServices(mobileServicesRef, isMobile, isLoading, safeServicesData);
  }, [isMobile, isLoading, safeServicesData]);

  // Refresh ScrollTrigger after all animations are set up
  useEffect(() => {
    return refreshScrollTrigger();
  }, []);

  // Show loading message while data is loading
  if (isLoading) {
    return (
      <div className={styles.pageBg}>
        <HeadingBox
          title={servicePageData.heading}
          description={servicePageData.description}
        />
        <div style={{ padding: "4rem", textAlign: "center" }}>
          <p style={{ color: "var(--color-primary)", fontSize: "1.2rem" }}>
            Loading services...
          </p>
        </div>
      </div>
    );
  }

  // Show message if no services data
  if (!safeServicesData || safeServicesData.length === 0) {
    return (
      <div className={styles.pageBg}>
        <HeadingBox
          title={servicePageData.heading}
          description={servicePageData.description}
        />
        <div style={{ padding: "4rem", textAlign: "center" }}>
          <p style={{ color: "var(--color-primary)", fontSize: "1.2rem" }}>
            No services available. Please check the API connection.
          </p>
          <p style={{ color: "var(--color-primary)", fontSize: "0.9rem", marginTop: "1rem", opacity: 0.7 }}>
            Check the browser console for error details.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.pageBg}>
      <HeadingBox
        title={servicePageData.heading}
        description={servicePageData.description}
      />
      {/* Desktop */}
      {!isMobile && (
        <>
          <div ref={servicesGridRef} className={styles.servicesGrid}>
            {safeServicesData.map((s, idx) => (
              <div
                className={
                  styles.mainCard + (idx === selectedIdx ? " " + styles.selected : "")
                }
                key={s.title}
                onClick={() => {
                  setSelectedIdx(idx);
                  setOpenSubService(null);
                }}
                tabIndex={0}
                role="button"
                aria-label={s.title}
              >
                <div className={styles.mainContent}>
                  <img 
                    src={s.image} 
                    alt={s.title} 
                    className={styles.mainImage}
                    onError={(e) => {
                      console.error(`Failed to load image for ${s.title}:`, s.image, e);
                    }}
                    onLoad={() => {
                      console.log(`Successfully loaded image for ${s.title}`);
                    }}
                  />
                  <div className={styles.mainTitle}>{s.title}</div>
                  <div className={styles.mainDesc}>{s.description}</div>
                </div>
                <Arrow
                  selected={idx === selectedIdx}
                  onClick={() => {
                    setSelectedIdx(idx);
                    setOpenSubService(null);
                  }}
                />
              </div>
            ))}
          </div>
          <div 
            ref={subServicesRef}
            key={selected?.title || 'none'}
            className={selected && selected.services && selected.services.length > 0 ? styles.desktopSubServicesWrapper + " " + styles.desktopSubServicesOpen : styles.desktopSubServicesWrapper}
          >
            {selected && selected.services && selected.services.length > 0 && (
              <>
                <div className={styles.subGridTitle}>{selected.title} Services</div>
                <ServicesList
                  services={selected.services}
                  cols={cols}
                  openSubService={openSubService}
                  setOpenSubService={setOpenSubService}
                  SubArrow={SubArrow}
                />
              </>
            )}
          </div>
        </>
      )}
      {/* Mobile */}
      {isMobile && (
        <>
          <div ref={mobileServicesRef} className={styles.servicesListMobile}>
            {safeServicesData.map((s, idx) => (
              <div key={s.title} className={styles.mobileMainCardWrapper}>
                <div
                  className={
                    styles.mainCardMobile + (idx === selectedIdx ? " " + styles.selected : "")
                  }
                  onClick={() => {
                    // Toggle: if already selected, deselect; otherwise select
                    if (selectedIdx === idx) {
                      setSelectedIdx(null);
                      setOpenSubService(null);
                    } else {
                      setSelectedIdx(idx);
                      setOpenSubService(null);
                    }
                  }}
                  tabIndex={0}
                  role="button"
                  aria-label={s.title}
                >
                  <div className={styles.mainContent}>
                    <img 
                      src={s.image} 
                      alt={s.title} 
                      className={styles.mainImage}
                      onError={(e) => {
                        console.error(`Failed to load image for ${s.title}:`, s.image, e);
                      }}
                      onLoad={() => {
                        console.log(`Successfully loaded image for ${s.title}`);
                      }}
                    />
                    <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', flex: 1, padding: '0.5em 0' }}>
                      <div className={styles.mainTitle}>{s.title}</div>
                      <div className={styles.mainDesc}>{s.description}</div>
                    </div>
                  </div>
                  <Arrow
                    selected={idx === selectedIdx}
                    onClick={(e) => {
                      e.stopPropagation();
                      // Toggle: if already selected, deselect; otherwise select
                      if (selectedIdx === idx) {
                        setSelectedIdx(null);
                        setOpenSubService(null);
                      } else {
                        setSelectedIdx(idx);
                        setOpenSubService(null);
                      }
                    }}
                  />
                </div>
                <div 
                  key={s.title}
                  className={idx === selectedIdx ? styles.mobileSubServicesWrapper + " " + styles.mobileSubServicesOpen : styles.mobileSubServicesWrapper}
                >
                  {idx === selectedIdx && selected && selected.services && selected.services.length > 0 && (
                    <>
                      <div className={styles.subGridTitleMobile}>{selected.title} Services</div>
                      <ServicesList
                        services={selected.services}
                        cols={3}
                        openSubService={openSubService}
                        setOpenSubService={setOpenSubService}
                        SubArrow={SubArrow}
                      />
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default ServicesPage;
